import { Input } from "@base-ui/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { Search } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import type { SearchableField } from "./crud-table";
import { FormSheet } from "./form-sheet";

type TableToolbarProps = {
	selectedCount: number;
	searchField: string;
	setSearchField: (searchField: string) => void;
	searchableFields: SearchableField[];
	globalFilter: string;
	setGlobalFilter: (glovalFilter: string) => void;
};

export default function TableToolbar({
	selectedCount,
	searchField,
	setSearchField,
	searchableFields,
	globalFilter,
	setGlobalFilter,
}: TableToolbarProps) {
	const intl = useIntl();
	const activeFieldLabel =
		searchableFields
			.find((f) => f.value === searchField)
			?.label?.toString()
			.toLowerCase() ??
		intl.formatMessage({ id: "table.search.fieldFallback", defaultMessage: "field" });

	return (
		<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
			<div className="flex flex-1 items-center gap-2">
				<div className="flex h-10 w-full max-w-md items-stretch overflow-hidden rounded-md border border-border bg-input transition-colors focus-within:border-ring focus-within:ring-1 focus-within:ring-ring">
					<Select
						value={searchField}
						onValueChange={(value) => {
							if (value) setSearchField(value);
						}}
					>
						<SelectTrigger className="h-full w-[140px] rounded-none border-0 border-r border-border bg-secondary/60 px-3 text-xs font-medium text-muted-foreground shadow-none focus:ring-0">
							<SelectValue
								placeholder={intl.formatMessage({
									id: "table.search.fieldPlaceholder",
									defaultMessage: "Field",
								})}
							/>
						</SelectTrigger>
						<SelectContent>
							{searchableFields.map((f) => (
								<SelectItem key={f.value} value={f.value} className="w-full">
									{f.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="relative flex-1">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={globalFilter}
							onChange={(e) => setGlobalFilter(e.target.value)}
							placeholder={intl.formatMessage(
								{
									id: "table.search.placeholder",
									defaultMessage: "Search by {field}...",
								},
								{ field: activeFieldLabel }
							)}
							className="h-full  w-full rounded-none border-0 bg-transparent pl-9 shadow-none ring-0 focus-visible:ring-0 focus-visible:outline-0"
							autoComplete="off"
						/>
					</div>
				</div>
			</div>
			<div className="flex items-center gap-2">
				{selectedCount > 0 && (
					<span className="text-xs text-muted-foreground">
						<FormattedMessage
							id="table.selectedCount"
							defaultMessage="{count} selected"
							values={{ count: selectedCount }}
						/>
					</span>
				)}
				<FormSheet />
			</div>
		</div>
	);
}
